/*
 * The font the anonymized preview sets a substitute in: one that looks like the text it replaces.
 *
 * The export sets a substitute in the document's own embedded font. pdf.js has already loaded that
 * font into the page's `document.fonts` to paint it, under its own handle ("g_d0_f2"), so the
 * preview sets the substitute in the very same glyphs (`embeddedSubstituteFont`). Font names and
 * descriptors are no guide to it: many PDFs name Arial "CIDFont+F1" and flag it serif.
 *
 * Behind it, for a font pdf.js has not loaded (yet) or a char its subset lacks, comes a look-alike by
 * the PDF's font name ("BCDHEE+Arial-BoldMT") from the reviewer's machine: the Microsoft core fonts
 * on Windows, their metric-compatible Liberation, Carlito and Caladea twins on Linux. Nothing is
 * downloaded. Where the embedded font lacks a glyph of the substitute the export uses its bundled
 * DejaVu Sans, and so does the preview (`FALLBACK_SUBSTITUTE_FONT`).
 */

/** A CSS font to set a substitute in. */
export interface SubstituteFont {
  /** A `font-family` stack. */
  family: string;
  weight: number;
  style: 'normal' | 'italic';
}

const SANS = 'Arial, "Liberation Sans", Arimo, Helvetica, sans-serif';
const SERIF = '"Times New Roman", "Liberation Serif", Tinos, Times, serif';
const MONO = '"Courier New", "Liberation Mono", Cousine, Courier, monospace';

/** For a run whose font is not known (yet): plain sans-serif, the commonest document text. */
export const DEFAULT_SUBSTITUTE_FONT: SubstituteFont = { family: SANS, weight: 400, style: 'normal' };

/** The export's bundled font, for a finding whose substitute the document's own font cannot draw. */
export const FALLBACK_SUBSTITUTE_FONT: SubstituteFont = {
  family: '"DejaVu Sans", Verdana, sans-serif',
  weight: 400,
  style: 'normal',
};

/** Font name fragments (lower case, no separators) and the stacks that look like them. */
const FAMILIES: [RegExp, string][] = [
  [/arial|helvetica|arimo|liberationsans/, SANS],
  [/times|tinos|liberationserif/, SERIF],
  [/calibri|carlito/, 'Calibri, Carlito, "Segoe UI", Arial, sans-serif'],
  // Cambria Math is a symbol font; its text falls through to the generic family.
  [/cambria(?!math)|caladea/, 'Cambria, Caladea, Georgia, serif'],
  [/courier|cousine|liberationmono/, MONO],
  [/verdana/, 'Verdana, "DejaVu Sans", sans-serif'],
  [/tahoma/, 'Tahoma, Verdana, sans-serif'],
  [/georgia/, 'Georgia, serif'],
  [/segoe/, '"Segoe UI", Arial, sans-serif'],
  [/dejavusans/, '"DejaVu Sans", Verdana, sans-serif'],
];

const GENERIC: Record<string, string> = { serif: SERIF, monospace: MONO };

/**
 * The substitute font for a PDF font name as pdf.js reports it, subset prefix and all.
 *
 * `generic` is pdf.js's own guess of the family class (`serif`, `sans-serif`, `monospace`) and
 * decides for a font this list does not know.
 */
export const substituteFont = (pdfFontName: string | undefined, generic: string | undefined): SubstituteFont => {
  const name = (pdfFontName ?? '').replace(/^[A-Z]{6}\+/, '').toLowerCase();
  const compact = name.replace(/[\s_,-]/g, '');
  const weight = /black|heavy/.test(compact)
    ? 900
    : /semibold|demibold|demi/.test(compact)
      ? 600
      : /bold/.test(compact)
        ? 700
        : /light/.test(compact)
          ? 300
          : 400;
  const style = /italic|oblique/.test(compact) ? 'italic' : 'normal';
  const family = FAMILIES.find(([pattern]) => pattern.test(compact))?.[1] ?? GENERIC[generic ?? ''] ?? SANS;
  return { family, weight, style };
};

/**
 * The document's own font, as pdf.js registered it under `loadedName`, with `lookAlike` behind it for
 * a char the subset lacks. The face is used as registered, normal weight and slant: asking for bold
 * would make the browser embolden an already bold face.
 */
export const embeddedSubstituteFont = (loadedName: string, lookAlike: SubstituteFont): SubstituteFont => ({
  family: `"${loadedName}", ${lookAlike.family}`,
  weight: 400,
  style: 'normal',
});

/** The CSS `font` shorthand for measuring or drawing text in `font` at `size` pixels. */
export const cssFont = (font: SubstituteFont, size: number): string =>
  `${font.style} ${font.weight} ${size}px ${font.family}`;
